import { createContext, useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { updateThemeVariables } from '@/utils/themeUtils';
import { Loader } from 'lucide-react';
import { getBrandingByDomain } from '@/api/branding/api';
import { BrandingErrorPage } from '@/components/ui/BrandingErrorPage';

interface BrandingConfig {
  company_name: string;
  logo_url: string;
  dark_logo_url?: string;
  icon_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
}

interface TenantContextType {
  branding: BrandingConfig | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBranding] = useState<BrandingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTenantBranding = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }

    try {
      const hostname = window.location.hostname;
      const response = await getBrandingByDomain(hostname);
      
      if (response.error || !response.data) {
        throw new Error(response.error || 'Unable to load configuration');
      }

      const data = response.data;
      setBranding(data);
      
      // Store branding in global window for theme switching
      window.__BRANDING_CONFIG__ = {
        primary_color: data.primary_color,
        secondary_color: data.secondary_color
      };
      
      updateThemeVariables({
        primary_color: data.primary_color,
        secondary_color: data.secondary_color
      });
      
      // Set Sentry context for branding information
      Sentry.setContext('branding', {
        company_name: data.company_name,
        domain: hostname,
        has_logo: !!data.logo_url,
        has_icon: !!data.icon_url,
        has_favicon: !!data.favicon_url,
      });
      
      // Add domain as a tag for easier filtering
      Sentry.setTag('tenant.domain', hostname);
      Sentry.setTag('tenant.company', data.company_name);

    } catch (err) {
      console.error('Configuration loading error:', err);
      setError(new Error('Unable to load configuration'));
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchTenantBranding();
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !branding) {
    return <BrandingErrorPage />;
  }

  return (
    <TenantContext.Provider value={{ 
      branding, 
      isLoading, 
      error,
      refetch: fetchTenantBranding 
    }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
} 